import type { TurnstileInstance } from '@marsidev/react-turnstile'
import { Turnstile } from '@marsidev/react-turnstile'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useColorModeContext } from 'src/components/Common/ColorModeProvider'
import { TurnstileWidgetMode } from 'src/services/apollo/generated/graphql'
import { CaptchaError } from 'src/utils/error'
import type {
  CaptchaToken,
  InvisibleCaptchaProps,
  InvisibleCaptchaRef,
  VisibleCaptchaProps,
  VisibleCaptchaRef,
} from './Captcha'
import { getTurnstileSiteKey } from './turnstileKey'

export const InvisibleTurnstileWrapper = forwardRef<
  InvisibleCaptchaRef,
  InvisibleCaptchaProps
>(({ setCaptchaReady, action }, ref) => {
  const turnstileRef = useRef<TurnstileInstance>(null)
  const lastErrorRef = useRef<string>('')
  useImperativeHandle(ref, () => {
    return {
      getToken: async () => {
        const turnstile = turnstileRef.current
        if (!turnstile) {
          throw new CaptchaError('turnstile not prepared')
        }
        const getToken = async () => {
          try {
            return await turnstile.getResponsePromise(5000, 250)
          } catch (err) {
            throw new CaptchaError(`trunstile error: ${lastErrorRef.current}`)
          }
        }
        const token = await getToken()
        if (token == null) {
          throw new CaptchaError('turnstile token acquisition failed')
        }
        return {
          token,
          provider: 'turnstile',
          turnstileWidgetMode: TurnstileWidgetMode.Invisible,
        }
      },
      reset: () => {
        turnstileRef.current?.reset()
      },
    }
  }, [])
  return (
    <Turnstile
      siteKey={getTurnstileSiteKey(TurnstileWidgetMode.Invisible)}
      options={{
        size: 'invisible',
        action,
      }}
      onWidgetLoad={() => {
        setCaptchaReady?.(true)
      }}
      onError={(errorCode: string) => {
        console.warn('turnstile error:', errorCode)
        lastErrorRef.current = errorCode
      }}
      ref={turnstileRef}
    />
  )
})

export const VisibleTurnstileWrapper = forwardRef<
  VisibleCaptchaRef,
  VisibleCaptchaProps
>(
  (
    { setToken, action, turnstileWidgetMode = TurnstileWidgetMode.Managed },
    ref,
  ) => {
    const turnstileRef = useRef<TurnstileInstance>(null)
    useImperativeHandle(ref, () => {
      return {
        reset: () => {
          turnstileRef.current?.reset()
        },
      }
    })
    const [internalToken, setInternalToken] = useState<
      CaptchaToken | undefined
    >()
    useEffect(() => {
      setToken?.(internalToken)
    }, [internalToken, setToken])
    const { themeType } = useColorModeContext()
    const { i18n } = useTranslation()
    const language = i18n.language
    const turnstileLanguage = language === 'zh' ? 'zh-tw' : language
    return (
      <Turnstile
        siteKey={getTurnstileSiteKey(turnstileWidgetMode)}
        className="!min-w-0"
        options={{
          size: 'flexible',
          action,
          theme: themeType,
          language: turnstileLanguage,
        }}
        onError={(errorCode: string) => {
          console.warn('turnstile error:', errorCode)
          setInternalToken(undefined)
        }}
        onExpire={() => {
          setInternalToken(undefined)
        }}
        onSuccess={(token: string) => {
          setInternalToken({
            token,
            provider: 'turnstile',
            turnstileWidgetMode: turnstileWidgetMode,
          })
        }}
        ref={turnstileRef}
      />
    )
  },
)
